import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  contentChildren,
  effect,
  inject,
  Injector,
  input,
  NgZone,
  OnDestroy,
  OnInit,
  output,
} from '@angular/core';
import { outputToObservable, toObservable } from '@angular/core/rxjs-interop';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { skip, take } from 'rxjs';

import { GoogleMapComponent } from '../map/google-map.component';
import { MapAdvancedMarkerComponent } from '../map-advanced-marker/map-advanced-marker.component';

import { BlazeClusterRenderer, BlazeClusterRendererStyle } from './cluster-renderer';

@UntilDestroy()
@Component({
  selector: 'be-map-marker-clusterer',
  template: `<ng-content />`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MapMarkerClustererComponent implements OnInit, AfterContentInit, OnDestroy {
  private readonly mapComponent = inject(GoogleMapComponent);
  private readonly ngZone = inject(NgZone);
  private readonly injector = inject(Injector);

  enabled = input(false);
  clusterRendererStyle = input<BlazeClusterRendererStyle>({
    fillColor: 'var(--map-cluster-fill-color)',
    borderColor: 'var(--map-cluster-border-color)',
    textColor: 'var(--map-cluster-text-color)',
  });

  markerClustererInitialized = output();

  markers = contentChildren(MapAdvancedMarkerComponent, { descendants: true });

  private markerClusterer: MarkerClusterer | null = null;
  private readonly currentMarkers = new Set<google.maps.marker.AdvancedMarkerElement>();

  constructor() {
    effect(() => {
      if (!this.markerClusterer || !this.mapComponent.googleMap) {
        return;
      }

      this.toggleMarkerClusterer(this.markerClusterer, this.mapComponent.googleMap, this.enabled());
    });
  }

  ngOnInit(): void {
    this.ngZone.runOutsideAngular(() => {
      this.mapComponent.resolveMap().then((map) => {
        const renderer = new BlazeClusterRenderer(this.clusterRendererStyle());
        this.markerClusterer = new MarkerClusterer({
          markers: [],
          map: this.enabled() ? map : null,
          renderer,
        });
        this.markerClustererInitialized.emit();
      });
    });
  }

  ngAfterContentInit(): void {
    if (this.markerClusterer) {
      this.watchForMarkerChanges();
    } else {
      outputToObservable(this.markerClustererInitialized)
        .pipe(take(1), untilDestroyed(this))
        .subscribe(() => this.watchForMarkerChanges());
    }
  }

  ngOnDestroy(): void {
    this.markerClusterer?.setMap(null);
  }

  private watchForMarkerChanges(): void {
    this.ngZone.runOutsideAngular(() => {
      this.getInternalMarkers().then((markers) => {
        const initialMarkers: google.maps.marker.AdvancedMarkerElement[] = [];
        for (const marker of markers) {
          this.currentMarkers.add(marker);
          initialMarkers.push(marker);
        }
        this.markerClusterer?.addMarkers(initialMarkers);
      });
    });

    toObservable(this.markers, { injector: this.injector })
      .pipe(skip(1), untilDestroyed(this))
      .subscribe(() => {
        this.ngZone.runOutsideAngular(() => {
          this.getInternalMarkers().then((markers) => {
            const newMarkers = new Set(markers);
            const markersToAdd: google.maps.marker.AdvancedMarkerElement[] = [];
            const markersToRemove: google.maps.marker.AdvancedMarkerElement[] = [];

            for (const marker of markers) {
              if (!this.currentMarkers.has(marker)) {
                this.currentMarkers.add(marker);
                markersToAdd.push(marker);
              }
            }

            for (const marker of Array.from(this.currentMarkers)) {
              if (!newMarkers.has(marker)) {
                markersToRemove.push(marker);
              }
            }

            this.markerClusterer?.addMarkers(markersToAdd, true);
            this.markerClusterer?.removeMarkers(markersToRemove, true);
            this.markerClusterer?.render();

            for (const marker of markersToRemove) {
              this.currentMarkers.delete(marker);
            }
          });
        });
      });
  }

  private getInternalMarkers(): Promise<google.maps.marker.AdvancedMarkerElement[]> {
    return Promise.all(this.markers().map((component) => component.resolveMarker()));
  }

  private toggleMarkerClusterer(
    clusterer: MarkerClusterer,
    map: google.maps.Map,
    enabled: boolean,
  ): void {
    this.ngZone.runOutsideAngular(() => {
      if (enabled) {
        clusterer.addMarkers(Array.from(this.currentMarkers), true);
        clusterer.render();
      } else {
        clusterer.clearMarkers();
        for (const marker of Array.from(this.currentMarkers)) {
          marker.map = map;
        }
      }
    });
  }
}
